/**
 * ProgressBar atom — track + fill only (no labels, tags, or icons).
 *
 * Prefer the molecule (`@/components/ui/molecules/ProgressBar`) for labeled/usage UI.
 * Use this atom for bare meters or inside the molecule.
 *
 * Manages: sizes (xs, sm, md, lg), semantic fill, indeterminate, palette, overBackground.
 *
 * @example
 * <ProgressBar value={50} max={100} size="md" />
 * <ProgressBar isIndeterminate size="sm" />
 */

import React from 'react';
import { cn } from '@/utils/cn';
import {
  getBrandFillGradient,
  getFillClass,
  getSemanticLevel,
  overBackgroundStyles,
  sizeStyles,
  unfilledOverlayStyles,
} from './ProgressBar.styles';
import type { ProgressBarProps } from './ProgressBar.types';

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  isIndeterminate = false,
  size = 'sm',
  palette = 'standard',
  semantic = 'usage',
  intent,
  overBackground = false,
  'aria-label': ariaLabel = 'Progress',
  className = '',
}) => {
  const indeterminate = isIndeterminate || value === undefined;
  const percent =
    indeterminate || max <= 0
      ? 0
      : Math.max(0, Math.min(100, Math.round(((value ?? 0) / max) * 100)));
  const unfilledPercent = Math.max(0, 100 - percent);
  const resolvedIntent = intent ?? getSemanticLevel(percent, semantic);
  const useBrandFill = !indeterminate && palette === 'brand';
  const fillClass = getFillClass(resolvedIntent, palette);
  const trackClass = overBackground ? overBackgroundStyles.track : 'bg-dark-invirtus/15';
  const heightClass = sizeStyles[size];
  const clampedValue = indeterminate ? undefined : Math.max(0, Math.min(max, value ?? 0));

  return (
    <div
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : max}
      aria-valuetext={indeterminate ? undefined : `${clampedValue} of ${max}`}
      aria-label={ariaLabel}
      className={cn(
        'relative w-full rounded-full overflow-hidden',
        trackClass,
        heightClass,
        className
      )}
    >
      {indeterminate ? (
        <div
          className={cn(
            'absolute left-0 top-0 h-full w-1/3 rounded-full',
            overBackground ? overBackgroundStyles.fill : fillClass,
            'animate-[progress-indeterminate_1.5s_ease-in-out_infinite]'
          )}
          aria-hidden
        />
      ) : useBrandFill ? (
        <>
          <div
            className="absolute inset-0 rounded-full transition-all duration-300"
            style={{ background: getBrandFillGradient(semantic) }}
            aria-hidden
          />
          <div
            className={cn('absolute right-0 top-0 h-full transition-all duration-300', overBackground ? unfilledOverlayStyles.overBackground : unfilledOverlayStyles.default, unfilledPercent === 100 ? 'rounded-full' : 'rounded-r-full')}
            style={{ width: `${unfilledPercent}%` }}
            aria-hidden
          />
        </>
      ) : (
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            overBackground ? overBackgroundStyles.fill : fillClass
          )}
          style={{ width: `${percent}%` }}
        />
      )}
    </div>
  );
};
